import React, { Component, ErrorInfo, ReactNode } from "react";
import classNames from "classnames";
import CiroCard from "./CiroCard";
import CiroButton from "./CiroButton";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import * as Sentry from "@sentry/react";

interface IErrorBoundary {
  children: ReactNode;
  className?: string;
  context?: Record<string, any>; // Additional context for error tracking
  fallback?: ReactNode;
  name?: string; // Component name for error tracking
  onReset?: () => void;
}

interface ErrorBoundaryState {
  error: Error | null;
  hasError: boolean;
}

// This is a class component (against codebase style) because error boundaries
// are only supported with class components as of React 18.
class CiroErrorBoundary extends Component<IErrorBoundary, ErrorBoundaryState> {
  constructor(props: IErrorBoundary) {
    super(props);
    this.state = {
      error: null,
      hasError: false,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      error,
      hasError: true,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const { name, context } = this.props;
    console.error(`Error caught by CiroErrorBoundary${name ? ` in ${name}` : ''}:`, error, errorInfo);
    
    // Report error to Sentry
    Sentry.withScope((scope) => {
      // Add component stack trace
      scope.setExtra("componentStack", errorInfo.componentStack);
      
      // Add component name if provided
      if (name) {
        scope.setTag("component", name);
      }
      
      // Add additional context if provided
      if (context) {
        Object.entries(context).forEach(([key, value]) => {
          scope.setExtra(key, value);
        });
      }
      
      Sentry.captureException(error);
    });
  }

  resetErrorBoundary = (): void => {
    this.props.onReset?.();
    this.setState({
      error: null,
      hasError: false,
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const isDev = process.env.NODE_ENV === 'development';
      const { name } = this.props;
      const errorMessage = this.state.error?.message || 'An unexpected error occurred';

      return (
        <div className={classNames("w-full", this.props.className)}>
          <CiroCard>
            <div className={classNames("flex", "flex-col", "items-center", "justify-center", "p-6", "text-center")}>
              <div className={classNames("mb-4", "text-red-500")}>
                <ExclamationTriangleIcon className={classNames("h-12", "w-12")} />
              </div>
              <h3 className={classNames("mb-2", "text-lg", "font-semibold", "text-gray-800")}>
                Something went wrong{name ? ` in ${name}` : ''}
              </h3>
              <p className={classNames("mb-4", "text-sm", "text-gray-600")}>
                {isDev ? errorMessage : 'We encountered an error.'}
              </p>
              
              {/* Show stack trace in development only */}
              {isDev && this.state.error && (
                <div className={classNames("mb-4", "w-full", "overflow-auto", "text-left")}>
                  <pre className={classNames("bg-gray-100", "p-4", "rounded", "text-xs", "text-red-600", "whitespace-pre-wrap")}>
                    {this.state.error.stack}
                  </pre>
                </div>
              )}
              
              <div className={classNames("mt-4")}>
                <CiroButton
                  analyticsField={"Clicked error boundary try again"}
                  onClick={this.resetErrorBoundary}
                >
                  Try again
                </CiroButton>
              </div>
            </div>
          </CiroCard>
        </div>
      );
    }

    return this.props.children;
  }
}

export default CiroErrorBoundary;
